import { Component, OnDestroy, OnInit, Input, ErrorHandler, ChangeDetectionStrategy, ChangeDetectorRef, ElementRef } from '@angular/core'
import { DomSanitizer, SafeUrl } from '@angular/platform-browser'

import { valitsePikkukuvanKoko } from '@lemon/core/utils/pikkukuva'

import { TositeKuvaCacheService } from './tosite-kuva-cache.service'
import { LEMONAID_CF_API, LemonHttpService } from 'app/_angular/service/lemon-http.service'

interface SafeUrlAndObjectUrl {
  safeUrl: SafeUrl
  objectUrl: string
}

@Component({
  selector: '[app-tosite-kuva]',
  templateUrl: './tosite-kuva.component.html',
  styleUrls: ['./tosite-kuva.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TositeKuvaComponent implements OnInit, OnDestroy {
  private _url: string = null
  @Input()
  set url(url: string) {
    const toCheck = url ?? null
    if (this._url !== toCheck) {
      this.ladataan = true
      this.eiKuvaa = false
      this._url = toCheck
      if (this._alustettu) {
        this._lataaJosNakyvissa()
      }
    }
  }
  get url(): string {
    return this._url
  }

  @Input() expandable: boolean = false
  @Input() thumbnail: boolean = false
  @Input() contain = false
  @Input() pikkukuva = false
  @Input() pikkukuvanLeveys = 82
  @Input() viivastetty = false

  urlToUse: Promise<SafeUrlAndObjectUrl> = null
  ladataan = true
  eiKuvaa = false
  isExpanded: boolean = false

  private _alustettu = false
  private _nakyvissa = false
  private _havainnoija: IntersectionObserver | null = null

  constructor(
    private _errorHandler: ErrorHandler,
    private _httpService: LemonHttpService,
    private _sanitizer: DomSanitizer,
    private _tositeKuvaCacheService: TositeKuvaCacheService,
    private _elementRef: ElementRef<HTMLElement>,
    private _changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this._alustettu = true
    if (!this.viivastetty || typeof IntersectionObserver === 'undefined') {
      this._nakyvissa = true
      this._lataaJosNakyvissa()
      return
    }
    this._havainnoija = new IntersectionObserver(
      havainnot => {
        if (!havainnot.some(havainto => havainto.isIntersecting)) {
          return
        }
        this._lopetaHavainnointi()
        this._nakyvissa = true
        this._lataaJosNakyvissa()
        this._changeDetectorRef.markForCheck()
      },
      { rootMargin: '200px' },
    )
    this._havainnoija.observe(this._elementRef.nativeElement)
  }

  ngOnDestroy(): Promise<void> {
    this._lopetaHavainnointi()
    return this._destroyObjectUrl()
  }

  onLoad(event: Event) {
    this.ladataan = false
  }

  onError(event: Event) {
    console.log('error', event)
    if (this.pikkukuva) {
      this.ladataan = false
      this.eiKuvaa = true
      return
    }
    if (this.urlToUse === null) {
      this.ladataan = true
      const returnValue: SafeUrlAndObjectUrl = {
        objectUrl: null,
        safeUrl: '/assets/noimage.png',
      }
      this.urlToUse = Promise.resolve(returnValue)
    }
  }

  public setExpandedState() {
    if (this.expandable) {
      this.isExpanded = !this.isExpanded
    }
  }

  private _lataaJosNakyvissa() {
    if (this._nakyvissa) {
      this.urlToUse = this._transform(this._url)
    }
  }

  private _lopetaHavainnointi() {
    this._havainnoija?.disconnect()
    this._havainnoija = null
  }

  private _destroyObjectUrl(): Promise<void> {
    if (this.urlToUse) {
      return this.urlToUse
        .then(urli => {
          if (urli && urli.objectUrl) {
            // console.log('destory', urli.objectUrl)
            URL.revokeObjectURL(urli.objectUrl)
          }
          // this.urlToUse = null
        })
        .catch(err => {
          this._errorHandler.handleError(err)
          // this.urlToUse = null
        })
    }
    return Promise.resolve()
  }

  private async _getBlob(url: string): Promise<Blob> {
    // console.log('Try find!', url)

    const fromAddedCache = this._tositeKuvaCacheService.addedImageCache.get(url)
    if (fromAddedCache) {
      // console.log('FOUND!', fromAddedCache.annaKuvanPromise())
      return fromAddedCache.annaKuvanPromise()
    }

    const pikkukuvanKoko = this.pikkukuva ? valitsePikkukuvanKoko(window.devicePixelRatio, this.pikkukuvanLeveys) : null
    const verkkovalimuistinAvain = pikkukuvanKoko ? `${url}|${pikkukuvanKoko.l}x${pikkukuvanKoko.k}` : url

    const fromNetworkCache = this._tositeKuvaCacheService.networkImageCache.get(verkkovalimuistinAvain)
    if (fromNetworkCache) {
      return fromNetworkCache
    }

    const fromLazyPdfCache = this._tositeKuvaCacheService.annaKuvaPdfCachesta(url)
    if (fromLazyPdfCache) {
      return fromLazyPdfCache.annaKuvanPromise()
    }

    if (pikkukuvanKoko) {
      const pikkukuva = await this._httpService.getBinary(`/kuittiLataaSkaalattuKuva?a=${encodeURIComponent(url)}&l=${pikkukuvanKoko.l}&k=${pikkukuvanKoko.k}`, LEMONAID_CF_API)
      this._tositeKuvaCacheService.networkImageCache.set(verkkovalimuistinAvain, pikkukuva)
      return pikkukuva
    }

    // Fetch from network
    if (this.thumbnail) {
      const b = await this._httpService.getBinary('/kuittiLataaSkaalattuKuva?a=' + encodeURIComponent(url), LEMONAID_CF_API)
      this._tositeKuvaCacheService.networkImageCache.set(url, b)
      return b
    }

    const blobby = await this._httpService.getBinary('/kuittiLataaKuva?a=' + encodeURIComponent(url), LEMONAID_CF_API)
    this._tositeKuvaCacheService.networkImageCache.set(url, blobby)
    return blobby
  }

  private async _transform(url: string): Promise<SafeUrlAndObjectUrl> {
    try {
      // Destroy old object url if one exists
      await this._destroyObjectUrl()

      if (!url) {
        return null
      }

      if (url.startsWith('/assets/')) {
        const ret: SafeUrlAndObjectUrl = { objectUrl: null, safeUrl: url }
        return ret
      }

      const blobby = await this._getBlob(url)

      // Create object url + sanitize
      const objectUrl = URL.createObjectURL(blobby)
      const returnValue: SafeUrlAndObjectUrl = {
        objectUrl: objectUrl,
        safeUrl: this._sanitizer.bypassSecurityTrustUrl(objectUrl),
      }
      // console.log('for', url, returnValue)
      return returnValue
    } catch (err) {
      this._errorHandler.handleError(err)
      if (this.pikkukuva) {
        if (url === this._url) {
          this.ladataan = false
          this.eiKuvaa = true
        }
        return null
      }
      const returnValue: SafeUrlAndObjectUrl = {
        objectUrl: null,
        safeUrl: '/assets/noimage.png',
      }
      return returnValue
    }
  }
}
